<template>
  <v-card class="mt-4 pa-4">
    <div class="d-flex justify-end">
      <mf-search v-model="search" dense hide-details placeholder="Buscar produto" autofocus />
    </div>
    <div>
      <mf-info-card v-if="showInfoCard" :vHtml="infoCardContent" class="my-3" />
    </div>
    <div v-if="ungroupedProducts && ungroupedProducts.length > 0">
      <h2 v-if="offerType !== 'combo_offer_sales'" class="mb-4">Produtos desagrupados</h2>
      <component
        :is="getComponent"
        class="mt-4"
        :products="ungroupedProducts"
        :validations="$v"
        type="ungrouped"
        :has-segments="hasSegments"
        :has-offer-sales="hasOfferSales"
        :search="search"
        @remove-item="removeItem"
      />
    </div>

    <div v-if="groupedProducts && groupedProducts.length > 0" :class="ungroupedProducts && ungroupedProducts.length > 0 ? 'mt-8' : ''">
      <h2 class="mb-0">Grupos de produtos</h2>

      <div v-for="group in groupedProducts" :key="group._id">
        <hr class="my-4" />
        <div class="d-flex align-center justify-space-between">
          <h2>{{ group.name }}</h2>
          <mf-button text color="vermelho-d20" label="Remover grupo" icon="delete" @click="removeGroup(group._id)" />
        </div>
        <component
          :is="getComponent"
          class="mt-4"
          :products="group.products"
          type="grouped"
          :group="group"
          :validations="$v"
          :has-segments="hasSegments"
          :has-offer-sales="hasOfferSales"
          :search="search"
          @remove-item="removeItem"
        />
      </div>
    </div>
  </v-card>
</template>

<script>
import { required, requiredIf } from 'vuelidate/lib/validators'
import { notOnlySpace } from '@/helpers/validators.js'
import { price1Validation, price2Validation } from '@/modules/campanhas/gestao-ofertas-v2/helpers/validators.js'

import AbsoluteDiscountTable from './AbsoluteDiscountTable.vue'
import PercentageDiscountTable from './PercentageDiscountTable.vue'
import TakePayTable from './TakePayTable.vue'
import ComboTable from './ComboTable.vue'

export default {
  name: 'OfferSaleTable',
  components: { AbsoluteDiscountTable, PercentageDiscountTable, TakePayTable, ComboTable },
  props: {
    offerType: {
      type: String,
      default: '',
    },
    allProducts: {
      type: Array,
      default: () => [],
    },
    hasSegments: {
      type: Boolean,
      default: false,
    },
    hasOfferSales: {
      type: Boolean,
      default: false,
    },
    campaign: {
      type: Object,
      default: () => {},
    },
    showInfoCard: {
      type: Boolean,
      default: false,
    },
    infoCardContent: {
      type: String,
      default: '',
    },
  },
  data: () => ({
    search: '',
  }),
  computed: {
    getComponent() {
      const components = {
        offer_sales: AbsoluteDiscountTable,
        percentage_offer_sales: PercentageDiscountTable,
        takepay_offer_sales: TakePayTable,
        combo_offer_sales: ComboTable,
      }

      return components[this.offerType]
    },
    ungroupedProducts() {
      if (!this.allProducts) return []
      if (this.offerType === 'combo_offer_sales') return this.allProducts
      return this.allProducts.filter((product) => !(product.isGroup ?? false))
    },
    groupedProducts() {
      if (!this.allProducts) return []
      return this.allProducts.filter((product) => product.isGroup)
    },
  },
  methods: {
    checkValidations() {
      this.$v.$touch()
      /* this.$refs[this.offerType].options.page = 1 */
      return new Promise((resolve) => {
        setTimeout(() => {
          this.sortProducts()
          resolve()
        }, 0)
      }).then(() => {
        if (this.$v.$error) {
          this.$snackbar({ message: 'Preencha todos os campos corretamente', snackbarColor: 'error' })
          return false
        } else {
          return true
        }
      })
    },
    async sortProducts() {
      const batchSize = 50
      const totalProducts = this.ungroupedProducts.length
      const validProducts = []
      const invalidProducts = []
      let currentIndex = 0

      const processBatch = async () => {
        for (let i = 0; i < batchSize && currentIndex < totalProducts; i++) {
          const isProductInvalid = this.$v.ungroupedProducts.$each[currentIndex].$error
          const product = this.ungroupedProducts[currentIndex]

          if (isProductInvalid) {
            invalidProducts.push(product)
          } else {
            validProducts.push(product)
          }
          currentIndex++
        }
        if (currentIndex < totalProducts) {
          await new Promise((resolve) => setTimeout(resolve, 0))
          processBatch()
        } else {
          this.campaign[this.offerType] = [...invalidProducts, ...validProducts, ...this.groupedProducts]
        }
      }
      await processBatch()
    },
    removeItem({ item, groupId }) {
      if (groupId) {
        const newOfferSales = this.campaign[this.offerType].map((offer) => {
          if (offer._id === groupId) {
            return {
              ...offer,
              products: offer.products
                .map((product) => {
                  if (product.sku !== item.sku) return product
                })
                .filter((item) => item),
            }
          }
          return offer
        })
        this.campaign[this.offerType] = newOfferSales
      } else {
        const newOfferSales = this.campaign[this.offerType]
          .map((offer) => {
            if (offer.sku !== item.sku) return offer
          })
          .filter((item) => item)
        this.campaign[this.offerType] = newOfferSales
      }
    },
    removeGroup(groupId) {
      const newOfferSales = this.campaign[this.offerType]
        .map((offer) => {
          if (offer._id !== groupId) return offer
        })
        .filter((item) => item)
      this.campaign[this.offerType] = newOfferSales
    },
  },
  validations() {
    let fields = {}

    if (this.offerType === 'offer_sales') {
      fields = {
        required: requiredIf(function () {
          return this.allInputsRequired
        }),
        $each: {
          sku: requiredIf(function (group) {
            return !group.products
          }),
          name: { required, notOnlySpace },
          price_1: { price1Validation: price1Validation(this.isFranchise, true) },
          price_2: { price2Validation: price2Validation(this.isFranchise, true) },
          products: {
            $each: {
              name: { required, notOnlySpace },
            },
          },
        },
      }
    } else if (this.offerType === 'percentage_offer_sales' || this.offerType === 'takepay_offer_sales') {
      fields = {
        required: requiredIf(function () {
          return this.allInputsRequired
        }),
        $each: {
          sku: requiredIf(function (group) {
            return !group.products
          }),
          name: { required, notOnlySpace },
          products: {
            $each: {
              name: { required, notOnlySpace },
            },
          },
        },
      }
    } else if (this.offerType === 'combo_offer_sales') {
      fields = {
        required: requiredIf(function () {
          return this.allInputsRequired
        }),
        $each: {
          name: { required, notOnlySpace },
        },
      }
    }
    return {
      ...(this.ungroupedProducts.length > 0
        ? {
            ungroupedProducts: { ...fields },
          }
        : {}),

      ...(this.groupedProducts.length > 0
        ? {
            groupedProducts: { ...fields },
          }
        : {}),
    }
  },
}
</script>

<style lang="scss" scoped>
hr {
  border: 1px solid $cinza-l60;
}

p {
  font-size: 14px;
  color: $cinza-0;
}

h2 {
  font-size: 16px;
  font-weight: 600;
  color: $cinza-d10;
}
::v-deep .v-input {
  max-width: 275px;
}
</style>
